import { Calculator, FlaskConical, Play, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ChartView } from "../../components/ChartView";
import { formatValue } from "../../services/chartOptions";
import {
  ApiError,
  whatIfApi,
  type WhatIfOptions,
  type WhatIfPeriod,
  type WhatIfResult,
} from "../../services/api";
import type { SelectedContext } from "../../services/storage";
import type { PanelResult } from "../../types";
import { PERIOD_LABELS } from "./scenarioFormat";

function message(error: unknown): string {
  return error instanceof ApiError
    ? error.message
    : "What-If Analysis could not be loaded.";
}

/** One certified metric, one percentage change: available for every domain. */
export function SingleMetricCalculator({
  selectedContext,
}: {
  selectedContext: SelectedContext | null;
}) {
  const [options, setOptions] = useState<WhatIfOptions | null>(null);
  const [metric, setMetric] = useState("");
  const [dimension, setDimension] = useState("");
  const [period, setPeriod] = useState<WhatIfPeriod>("all_available");
  const [relativePercent, setRelativePercent] = useState(10);
  const [result, setResult] = useState<WhatIfResult | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setResult(null);
    setOptions(null);
    setMetric("");
    setDimension("");
    if (!selectedContext) {
      setLoadingOptions(false);
      return () => {
        active = false;
      };
    }
    setLoadingOptions(true);
    setError("");
    whatIfApi
      .options(selectedContext.contextId, selectedContext.contextVersion)
      .then((next) => {
        if (active) setOptions(next);
      })
      .catch((caught) => {
        if (active) setError(message(caught));
      })
      .finally(() => {
        if (active) setLoadingOptions(false);
      });
    return () => {
      active = false;
    };
  }, [selectedContext]);

  const chartResult = useMemo<PanelResult | undefined>(() => {
    if (!result) return undefined;
    const rows = result.rows.map((row) => ({
      label: row.label,
      baseline: row.baseline,
      scenario: row.scenario,
    }));
    return {
      panelId: "what-if-comparison",
      status: "ok",
      columns: ["label", "baseline", "scenario"],
      rows,
      rowCount: rows.length,
      truncated: false,
    };
  }, [result]);

  async function runScenario() {
    if (!selectedContext || !metric) return;
    setRunning(true);
    setError("");
    try {
      setResult(
        await whatIfApi.evaluate({
          contextId: selectedContext.contextId,
          contextVersion: options?.contextVersion,
          metric,
          dimension: dimension || null,
          period,
          relativePercent,
          limit: 20,
        }),
      );
    } catch (caught) {
      setError(message(caught));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      {error && (
        <div className="scenario-error" role="alert">
          <TriangleAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="scenario-layout">
        <section className="panel scenario-controls">
          <div className="panel-heading">
            <div>
              <h2>Scenario setup</h2>
              <p>One lever, fully reversible</p>
            </div>
          </div>

          <label>
            Metric
            <select
              value={metric}
              onChange={(event) => {
                setMetric(event.target.value);
                setResult(null);
              }}
              disabled={loadingOptions || !options?.metrics.length}
            >
              {options?.metrics.length ? (
                <option value="">Choose a metric</option>
              ) : (
                <option value="">No supported metric</option>
              )}
              {options?.metrics.map((item) => (
                <option value={item.member} key={item.member} title={item.description || item.member}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Break down by <small>Optional</small>
            <select
              value={dimension}
              onChange={(event) => {
                setDimension(event.target.value);
                setResult(null);
              }}
              disabled={loadingOptions}
            >
              <option value="">No breakdown</option>
              {options?.dimensions.map((item) => (
                <option value={item.member} key={item.member}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Historical period
            <select
              value={period}
              onChange={(event) => {
                setPeriod(event.target.value as WhatIfPeriod);
                setResult(null);
              }}
              disabled={loadingOptions}
            >
              {(options?.periods ?? ["all_available"]).map((item) => (
                <option value={item} key={item}>
                  {PERIOD_LABELS[item]}
                </option>
              ))}
            </select>
          </label>

          <div className="scenario-lever">
            <div>
              <label htmlFor="scenario-percent">Relative change</label>
              <output htmlFor="scenario-percent">
                {relativePercent > 0 ? "+" : ""}
                {relativePercent}%
              </output>
            </div>
            <input
              id="scenario-percent"
              type="range"
              min="-100"
              max="100"
              step="1"
              value={relativePercent}
              onChange={(event) => {
                setRelativePercent(Number(event.target.value));
                setResult(null);
              }}
            />
            <div className="scenario-range-labels">
              <span>-100%</span><span>No change</span><span>+100%</span>
            </div>
          </div>

          <button
            type="button"
            className="button primary scenario-run"
            onClick={runScenario}
            disabled={!metric || running || loadingOptions}
          >
            {running ? (
              <><span className="button-spinner" /> Calculating…</>
            ) : (
              <><Play size={15} /> Run scenario</>
            )}
          </button>
        </section>

        <div className="content-stack scenario-results">
          {!result ? (
            <section className="panel scenario-blank">
              <FlaskConical size={42} />
              <h2>Ready to compare</h2>
              <p>
                Run the scenario to see baseline and simulated values side by
                side.
              </p>
            </section>
          ) : (
            <>
              <section className="scenario-kpis">
                <div className="panel">
                  <span>Governed baseline</span>
                  <strong>{formatValue(result.baselineTotal)}</strong>
                </div>
                <div className="panel simulated">
                  <span>Simulated scenario</span>
                  <strong>{formatValue(result.scenarioTotal)}</strong>
                </div>
                <div className="panel">
                  <span>Estimated difference</span>
                  <strong>{formatValue(result.deltaTotal)}</strong>
                </div>
              </section>

              <section className="panel scenario-comparison">
                <div className="panel-heading">
                  <div>
                    <h2>{result.metricLabel}</h2>
                    <p>Governed baseline compared with simulated scenario</p>
                  </div>
                  <span className="status-badge simulated">Simulated</span>
                </div>
                <ChartView
                  panelId="what-if-comparison"
                  chartType="bar"
                  encoding={{ x: "label", y: ["baseline", "scenario"], series: "" }}
                  result={chartResult}
                />
              </section>

              <section className="panel">
                <div className="panel-heading">
                  <div>
                    <h2>Comparison detail</h2>
                    <p>Values remain separate from certified actuals</p>
                  </div>
                </div>
                <div className="panel-table-wrap">
                  <table className="panel-table">
                    <thead>
                      <tr>
                        <th scope="col">Group</th>
                        <th scope="col" className="numeric">Baseline</th>
                        <th scope="col" className="numeric">Scenario</th>
                        <th scope="col" className="numeric">Difference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row, index) => (
                        <tr key={`${row.label}-${index}`}>
                          <td>{row.label}</td>
                          <td className="numeric">{formatValue(row.baseline)}</td>
                          <td className="numeric">{formatValue(row.scenario)}</td>
                          <td className="numeric">{formatValue(row.delta)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="panel scenario-notes">
                <div>
                  <Calculator size={18} />
                  <div>
                    <h2>Assumptions</h2>
                    <ul>
                      {result.assumptions.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </div>
                </div>
                <p className="scenario-disclaimer">{result.disclaimer}</p>
              </section>
            </>
          )}
        </div>
      </div>
    </>
  );
}
